import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Check, Zap, ArrowRight, Home, Building2, HardHat, Sparkles, Smartphone, ClipboardCheck, Users, DollarSign, Clock, Calendar, Mail, Phone, MapPin, User } from 'lucide-react';
import { services, contactInfo } from '../data/mock';

const iconMap = {
  Home,
  Building2,
  HardHat,
  Sparkles,
  Smartphone,
  ClipboardCheck,
  Users,
  DollarSign,
  Clock
};

const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
  </svg>
);

const ServiceDetail = () => {
  const { serviceId } = useParams();
  const service = services.find(s => s.id === serviceId);
  
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    service: service?.title || '',
    date: '',
    time: '',
    phone: '',
    email: '',
    address: ''
  });

  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    
    // Google Ads Conversion Tracking
    if (window.gtag) {
      window.gtag('event', 'conversion', {
        'send_to': 'AW-17761693801/KUPZCNyatccbEOngt5VC',
        'value': 1.0,
        'currency': 'TRY'
      });
    }
    
    const serviceTitle = formData.service || 'Genel Teklif Talebi';
    const message = `🏠 *${serviceTitle} - Teklif Talebi*\n\n` +
      `👤 *Ad Soyad:* ${formData.name} ${formData.surname}\n` +
      `🧹 *Hizmet:* ${formData.service}\n` +
      `📅 *Tarih:* ${formData.date}\n` +
      `⏰ *Saat:* ${formData.time}\n` +
      `📞 *Telefon:* ${formData.phone}\n` +
      `📧 *E-posta:* ${formData.email}\n` +
      `📍 *Adres:* ${formData.address}`;
    
    const whatsappUrl = `https://wa.me/${contactInfo.whatsapp.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    
    setFormData({
      name: '',
      surname: '',
      service: service?.title || '',
      date: '',
      time: '',
      phone: '',
      email: '',
      address: ''
    });
  };

  if (!service) {
    return (
      <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #0f0f0f 100%)' }}>
        <div className="neon-grid"></div>
        <div className="neon-beams"></div>
        <div className="fixed inset-0 pointer-events-none z-0">
          <div className="absolute inset-0 wavy-lines-dark opacity-40"></div>
          <div className="absolute inset-0 flow-lines-dark opacity-30"></div>
          <div className="absolute inset-0 diagonal-lines opacity-20"></div>
        </div>
        
        <div className="relative z-10 min-h-screen flex items-center justify-center px-4 pt-32">
          <div className="text-center neon-card p-12 rounded-3xl max-w-md">
            <h1 className="text-3xl font-black text-white mb-4">Hizmet Bulunamadı</h1>
            <p className="text-gray-400 mb-6">Aradığınız hizmet mevcut değil.</p>
            <Link to="/">
              <Button className="bg-gradient-to-r from-neon-blue to-parliament-blue hover:from-parliament-blue hover:to-neon-blue text-white font-bold px-8 py-3">
                <ArrowLeft className="mr-2 w-5 h-5" />
                Ana Sayfaya Dön
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const Icon = iconMap[service.icon];

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #0f0f0f 100%)' }}>
      <div className="neon-grid"></div>
      <div className="neon-beams"></div>
      
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute inset-0 wavy-lines-dark opacity-40"></div>
        <div className="absolute inset-0 flow-lines-dark opacity-30"></div>
        <div className="absolute inset-0 diagonal-lines opacity-20"></div>
      </div>

      {/* Hero Image Section */}
      <section className="relative pt-32 pb-12 px-4 overflow-hidden">
        <div className="container mx-auto relative z-10">
          <Link 
            to="/" 
            className="inline-flex items-center text-neon-blue hover:text-white mb-6 transition-colors font-semibold"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Ana Sayfaya Dön
          </Link>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <div className="flex items-center gap-4 mb-4">
                <div className="w-20 h-20 bg-neon-blue/20 backdrop-blur-md rounded-xl flex items-center justify-center border-2 border-neon-blue/50">
                  {Icon && <Icon className="w-10 h-10 text-neon-blue" />}
                </div>
                <div>
                  <h1 className="text-4xl md:text-5xl font-black text-white" style={{
                    textShadow: '0 0 30px rgba(0, 212, 255, 0.5)'
                  }}>
                    {service.title}
                  </h1>
                  <p className="text-gray-400 mt-1">{service.shortDesc}</p>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-parliament-blue to-neon-blue rounded-3xl blur-xl opacity-30 animate-pulse"></div>
              <img
                src={service.image}
                alt={service.title}
                fetchpriority="high"
                className="rounded-2xl shadow-2xl w-full h-auto object-cover relative z-10 neon-card"
                style={{ maxHeight: '350px' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Service Details */}
      <section className="py-12 px-4 relative overflow-hidden">
        <div className="container mx-auto relative z-10">
          <div className="max-w-4xl mx-auto">
            <Card className="neon-card mb-8">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                  <Sparkles className="w-6 h-6 text-neon-blue" />
                  Hizmet Detayı
                </h2>
                <p className="text-gray-300 leading-relaxed text-base mb-8">
                  {service.fullDesc}
                </p>

                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Check className="w-5 h-5 text-neon-blue" />
                  Hizmet Kapsamı
                </h3>
                <ul className="space-y-3">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-gray-300">
                      <div className="w-6 h-6 rounded-full bg-neon-blue/20 border border-neon-blue/50 flex items-center justify-center mt-0.5 flex-shrink-0">
                        <Check className="w-4 h-4 text-neon-blue" />
                      </div>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-8 p-6 bg-neon-blue/10 rounded-lg border-l-4 border-neon-blue">
                  <h4 className="font-semibold text-white mb-2 flex items-center gap-2">
                    <Zap className="w-5 h-5 text-neon-blue" />
                    Neden C&D Clean?
                  </h4>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    Profesyonel ekibimiz, kalite standartlarımız ve dijital takip sistemimizle
                    size en iyi temizlik hizmetini sunuyoruz. Her projede müşteri memnuniyeti
                    bizim önceliğimizdir.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Quote Form Section */}
      <section className="py-12 px-4 relative overflow-hidden">
        <div className="container mx-auto relative z-10">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <span className="inline-block px-4 py-2 bg-green-500/20 text-green-400 rounded-full text-sm font-semibold mb-4 border border-green-500/40">
                HEMEN BAŞLAYIN
              </span>
              <h2 className="text-3xl md:text-5xl font-black text-white mb-4 leading-tight">
                {service.title} İçin Teklif Alın
              </h2>
              <p className="text-base md:text-lg text-gray-400 leading-relaxed">
                Bilgilerinizi doldurun, size en kısa sürede dönelim!
              </p>
            </div>

            <div className="neon-card p-6 md:p-8 rounded-2xl border-2 border-green-500/30" style={{
              boxShadow: '0 0 40px rgba(34, 197, 94, 0.3)'
            }}>
              <form onSubmit={handleFormSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-white flex items-center gap-2">
                      <User className="w-4 h-4 text-green-400" />
                      İsim *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleFormChange}
                      required
                      placeholder="Adınız"
                      className="bg-gray-800/50 border-green-500/30 text-white placeholder:text-gray-500 focus:border-green-400"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="surname" className="text-white flex items-center gap-2">
                      <User className="w-4 h-4 text-green-400" />
                      Soyisim *
                    </Label>
                    <Input
                      id="surname"
                      name="surname"
                      value={formData.surname}
                      onChange={handleFormChange}
                      required
                      placeholder="Soyadınız"
                      className="bg-gray-800/50 border-green-500/30 text-white placeholder:text-gray-500 focus:border-green-400"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="service" className="text-white flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-green-400" />
                    Hizmet *
                  </Label>
                  <Select 
                    value={formData.service} 
                    onValueChange={(value) => setFormData({...formData, service: value})}
                    required
                  >
                    <SelectTrigger className="bg-gray-800/50 border-green-500/30 text-white focus:border-green-400">
                      <SelectValue placeholder="Bir hizmet seçin..." />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-900 border-green-500/30">
                      {services.map(s => (
                        <SelectItem key={s.id} value={s.title} className="text-white hover:bg-green-500/20">
                          {s.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="date" className="text-white flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-green-400" />
                      Tarih *
                    </Label>
                    <Input
                      id="date"
                      name="date"
                      type="date"
                      value={formData.date}
                      onChange={handleFormChange}
                      required
                      className="bg-gray-800/50 border-green-500/30 text-white focus:border-green-400"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="time" className="text-white flex items-center gap-2">
                      <Clock className="w-4 h-4 text-green-400" />
                      Saat *
                    </Label>
                    <Input
                      id="time"
                      name="time"
                      type="time"
                      value={formData.time}
                      onChange={handleFormChange}
                      required
                      className="bg-gray-800/50 border-green-500/30 text-white focus:border-green-400"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-white flex items-center gap-2">
                      <Phone className="w-4 h-4 text-green-400" />
                      Telefon Numarası *
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleFormChange}
                      required
                      placeholder="05XX XXX XX XX"
                      className="bg-gray-800/50 border-green-500/30 text-white placeholder:text-gray-500 focus:border-green-400"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-white flex items-center gap-2">
                      <Mail className="w-4 h-4 text-green-400" />
                      E-posta *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleFormChange}
                      required
                      placeholder="ornek@email.com"
                      className="bg-gray-800/50 border-green-500/30 text-white placeholder:text-gray-500 focus:border-green-400"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address" className="text-white flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-green-400" />
                    Adresiniz *
                  </Label>
                  <Textarea
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleFormChange}
                    required
                    placeholder="Tam adresinizi yazınız..."
                    rows={3}
                    className="bg-gray-800/50 border-green-500/30 text-white placeholder:text-gray-500 focus:border-green-400 resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold text-base md:text-lg py-6 group relative overflow-hidden"
                  style={{
                    boxShadow: '0 0 25px rgba(34, 197, 94, 0.6), 0 0 50px rgba(34, 197, 94, 0.4)'
                  }}
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-green-600 via-green-500 to-green-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                  <span className="absolute inset-0 bg-white/10 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></span>
                  <WhatsAppIcon className="mr-2 w-6 h-6 relative z-10" />
                  <span className="relative z-10">WhatsApp ile Teklif Al</span>
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-2 transition-transform relative z-10" />
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Other Services Section */}
      <section className="py-12 px-4 relative overflow-hidden">
        <div className="container mx-auto relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
                Diğer Hizmetlerimiz
              </h2>
              <p className="text-gray-400">
                Size uygun diğer temizlik çözümlerimize göz atın
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {services
                .filter(s => s.id !== serviceId)
                .map(s => {
                  const OtherIcon = iconMap[s.icon];
                  return (
                    <Link
                      key={s.id}
                      to={`/hizmetler/${s.id}`}
                      className="block"
                    >
                      <Card className="neon-card h-full hover:scale-105 transition-all duration-300 group">
                        <CardContent className="p-6">
                          <div className="w-14 h-14 bg-neon-blue/20 backdrop-blur-md rounded-xl flex items-center justify-center mb-4 border border-neon-blue/50 group-hover:bg-neon-blue/40 transition-colors">
                            {OtherIcon && <OtherIcon className="w-7 h-7 text-neon-blue" />}
                          </div>
                          <h3 className="text-lg font-bold text-white mb-2 group-hover:text-neon-blue transition-colors">
                            {s.title}
                          </h3>
                          <p className="text-gray-400 text-sm mb-3 line-clamp-2">
                            {s.shortDesc}
                          </p>
                          <div className="flex items-center text-neon-blue font-semibold text-sm group-hover:gap-2 transition-all">
                            Detaylı İncele
                            <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-2 transition-transform" />
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  );
                })}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
